import { memo, useCallback, useEffect, useMemo, useRef } from "react"
import { AUTH_STATUS, useAuthStore } from "@/auth"
import { get } from "radash"
import { useLocation } from "react-router-dom"

import { v1GetBottomBar } from "@be-miniapp/api"
import { CACHE_KEYS } from "@be-miniapp/platform"
import { hasMeaningfulBottomNavigation } from "@be-miniapp/shared/utils/cache-guards"
import { removePackageNameInRoute } from "@be-miniapp/shared/utils/string"
import { useCacheFirst } from "@be-miniapp/ui"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import { useClientSetting } from "@/store/client-setting"
import { cn } from "@/utils/cn"

type BottomNavigationProps = {}

export const bottomNavigationQueryKey = ["bottom-navigation"] as const

let shouldAnimate = true

const BottomNavigation = memo(({}: BottomNavigationProps) => {
  const isAuthenticated =
    useAuthStore((state) => state.status) === AUTH_STATUS.AUTHENTICATED

  const {
    data: bottomBars,
    isLoading,
    isFetching,
    refetch,
  } = useCacheFirst({
    cacheKey: CACHE_KEYS.BOTTOM_NAV,
    queryKey: [...bottomNavigationQueryKey],
    queryFn: async () => {
      const resp = await v1GetBottomBar({})
      return resp.data
    },
    ttl: 7 * 24 * 60 * 60 * 1000,
    enabled: isAuthenticated,
    retry: 3,
    shouldCache: hasMeaningfulBottomNavigation,
  })

  const hasBottomBars = hasMeaningfulBottomNavigation(bottomBars)
  const isWaitingForBottomBars =
    isAuthenticated && !hasBottomBars && (isLoading || isFetching)

  const language = useClientSetting((state) => state.language)

  const pathname = useLocation().pathname
  const navigate = useNavigate()
  const retryAttemptRef = useRef(0)
  const hasScheduledAnimationRef = useRef(false)

  const handleNavigate = useCallback(
    (to: string | number) => {
      navigate(to, {
        animate: false,
      })
    },
    [navigate]
  )

  useEffect(() => {
    if (!hasBottomBars || hasScheduledAnimationRef.current) return

    hasScheduledAnimationRef.current = true
    const animationTimeout = window.setTimeout(() => {
      shouldAnimate = false
    }, 100)

    return () => {
      window.clearTimeout(animationTimeout)
    }
  }, [hasBottomBars])

  useEffect(() => {
    if (!isAuthenticated || hasBottomBars || isWaitingForBottomBars) return
    if (retryAttemptRef.current >= 3) return

    retryAttemptRef.current += 1
    const retryTimeout = window.setTimeout(() => {
      refetch()
    }, 1000 * retryAttemptRef.current)

    return () => {
      window.clearTimeout(retryTimeout)
    }
  }, [hasBottomBars, isAuthenticated, isWaitingForBottomBars, refetch])

  const tabs = useMemo(
    () =>
      (bottomBars?.icons ?? []).map((tab) => {
        let path = removePackageNameInRoute(tab.deep_link)
        if (!path.startsWith("/ride/home")) {
          path = `/ride/home${path}`
        }

        return {
          ...tab,
          path,
          label: (get(tab, `title_${language}`) as string | undefined) ?? "",
        }
      }),
    [bottomBars?.icons, language]
  )

  if (!hasBottomBars) {
    return null
  }

  const numberOfTabs = tabs.length

  return (
    <div
      className={cn(
        "safe-area-bottom absolute inset-x-0 bottom-0 z-50 bg-white/60 pt-2 shadow-[0px_8px_30px_0px_rgba(0,0,29,0.17)] backdrop-blur-md",
        shouldAnimate && "animate-slide-in-bottom-spring"
      )}
    >
      <div
        className={cn(
          "grid size-full",
          numberOfTabs === 3 && "grid-cols-3",
          numberOfTabs === 4 && "grid-cols-4",
          numberOfTabs === 5 && "grid-cols-5"
        )}
      >
        {tabs.map((tab, idx) => {
          return (
            <div
              key={tab.id}
              className={cn(
                "flex cursor-pointer flex-col items-center justify-center font-medium",
                pathname === tab.path
                  ? "font-semibold text-gray-800"
                  : "text-gray-400"
              )}
              onClick={() => {
                handleNavigate(tab.path)
              }}
            >
              <img
                key={tab.active_image}
                className={cn(
                  "size-5 object-cover",
                  idx === tabs.length - 1 && "rounded-full"
                )}
                src={
                  pathname === tab.path ? tab.active_image : tab.inactive_image
                }
              />

              <span className="line-clamp-1 text-[0.6rem] leading-5 max-fold:hidden">
                {tab.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
})

export { BottomNavigation }
